import axios from 'axios-observable';
import { AxiosObservable } from 'axios-observable/dist/axios-observable.interface';
import { PageableQuery, TalosPayload, UUID } from '../app/basicModels';
import { Indicator, IndicatorPage } from './models';
import { buildWorkspaceConfig } from '../methodologyDeep/scope';
import CONFIG from '../../settings';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { hasOperation, ObservationsConfigV2 } from '../observationsConfigV2';

const indicatorUrl = `${CONFIG.apiUrl}:${CONFIG.demeterPort}/api/v1/indicators`;
const cloneIndicatorUrl = `${CONFIG.apiUrl}:${CONFIG.demeterPort}/v1/indicators`;

const validateIndicatorExprUrl = `${CONFIG.apiUrl}:${CONFIG.demeterPort}/api/v1/indicator/verify`;

export const getIndicators = (_query: PageableQuery, companyId: UUID, showCanonicals: boolean): AxiosObservable<IndicatorPage> => {
  if (companyId) {
    return axios.get<IndicatorPage>(
      `${indicatorUrl}?page=${_query.pageable.page}&size=${_query.pageable.size}&company_id=${companyId}&show_canonicals=${showCanonicals}`
    );
  }
  return axios.get<IndicatorPage>(
    `${indicatorUrl}?page=${_query.pageable.page}&size=${_query.pageable.size}&show_canonicals=${showCanonicals}`
  );
};

export const getIndicator = (indicatorId: UUID): AxiosObservable<Indicator> => {
  return axios.get<Indicator>(`${indicatorUrl}/${indicatorId}`);
};

export const saveIndicator = (
  indicator: TalosPayload<Indicator>,
  isNew: boolean,
  defaultExpression: boolean,
  migratedWrites = '[]'
): AxiosObservable<Indicator> => {
  if (isNew) {
    if (defaultExpression && !hasOperation(migratedWrites, 'indicators.create')) {
      return createDefaultIndicator(indicator);
    }
    return createIndicator(indicator, migratedWrites);
  }
  return updateIndicator(indicator, migratedWrites);
};

export const validateIndicatorSimpleExpression = (indicator: Indicator): any => {
  return axios.post<any>(`${validateIndicatorExprUrl}`, indicator).pipe(
    map(response => response && { ...response.data, status: 200 }),
    catchError(error => of(error && error.response && error.response.data))
  );
};

export const createIndicator = (indicator: TalosPayload<Indicator>, migratedWrites = '[]'): AxiosObservable<Indicator> => {
  if (hasOperation(migratedWrites, 'indicators.create')) {
    return ObservationsConfigV2.createIndicator(indicator.payload);
  }

  return axios.post<Indicator>(`${indicatorUrl}`, indicator, buildWorkspaceConfig(indicator.payload.workspace_id ?? undefined));
};

export const createDefaultIndicator = (indicator: TalosPayload<Indicator>): AxiosObservable<Indicator> => {
  return axios.post<Indicator>(
    `${indicatorUrl}/default-expression`,
    indicator,
    buildWorkspaceConfig(indicator.payload.workspace_id ?? undefined)
  );
};

export const updateIndicator = (indicator: TalosPayload<Indicator>, migratedWrites = '[]'): AxiosObservable<Indicator> => {
  if (hasOperation(migratedWrites, 'indicators.update')) {
    return ObservationsConfigV2.updateIndicator(indicator.payload);
  }

  return axios.put<Indicator>(`${indicatorUrl}`, indicator, buildWorkspaceConfig(indicator.payload.workspace_id ?? undefined));
};

export const deleteIndicator = (indicator: TalosPayload<Indicator>, migratedWrites = '[]'): AxiosObservable<Indicator> => {
  if (hasOperation(migratedWrites, 'indicators.delete')) {
    const { id } = indicator.payload;
    if (!id) throw new Error('Indicator id is required');
    return ObservationsConfigV2.deleteIndicator(id);
  }

  return axios.put<Indicator>(`${indicatorUrl}/remove`, indicator, buildWorkspaceConfig(indicator.payload.workspace_id ?? undefined));
};

export const getIndicatorsForCompany = (_query: any): AxiosObservable<IndicatorPage> => {
  return axios.get<IndicatorPage>(
    `${indicatorUrl}?page=${_query.page.pageable.page}&size=${_query.page.pageable.size}&company_id=${
      _query.companyId
    }&show_canonicals=${true}`
  );
};

export const cloneIndicator = (
  indicatorId: UUID,
  companyId: UUID,
  enableCottonGrowth?: boolean,
  migratedWrites = '[]'
): AxiosObservable<Indicator> => {
  if (hasOperation(migratedWrites, 'indicators.clone-to-org')) {
    return ObservationsConfigV2.cloneIndicatorToOrg(indicatorId, companyId);
  }

  return enableCottonGrowth
    ? axios.post(`${cloneIndicatorUrl}/${indicatorId}/clone-to-company/${companyId}`)
    : axios.post(`${indicatorUrl}/${indicatorId}/clone-to-company/${companyId}`);
};

export const cloneIndicatorToWorkspace = (indicatorId: UUID, workspaceId: UUID, migratedWrites = '[]'): AxiosObservable<Indicator> => {
  if (hasOperation(migratedWrites, 'indicators.clone-to-workspace')) {
    return ObservationsConfigV2.cloneIndicatorToWorkspace(indicatorId, workspaceId);
  }

  return axios.post(`${indicatorUrl}/${indicatorId}/clone-to-workspace/${workspaceId}`, undefined, buildWorkspaceConfig(workspaceId));
};
